import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock, CalendarDays } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Single row for weekly schedule: day + optional start/end time */
export interface WeeklyScheduleSlot {
  day: string;
  start_time: string;
  end_time: string;
  available?: boolean;
}

interface FormsetFieldProps {
  /** Question code (e.g. "weekly_schedule") to determine formset variant */
  questionCode: string;
  /** Current value: array of slots for weekly_schedule */
  value?: WeeklyScheduleSlot[] | null;
  /** Callback when value changes */
  onChange: (value: WeeklyScheduleSlot[]) => void;
  /** Locale for labels */
  locale?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the question is required (for validation hint) */
  required?: boolean;
  /** Error state */
  hasError?: boolean;
}

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

const DAY_LABELS_EN: Record<string, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

const DAY_LABELS_AR: Record<string, string> = {
  sunday: 'الأحد',
  monday: 'الإثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة',
  saturday: 'السبت',
};

const WEEKEND_DAYS = new Set(['friday', 'saturday']);
const WEEKDAY_DAYS = new Set(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday']);

const DEFAULT_SLOTS: WeeklyScheduleSlot[] = DAY_KEYS.map((day) => ({
  day,
  start_time: WEEKDAY_DAYS.has(day) ? '09:00' : '',
  end_time: WEEKDAY_DAYS.has(day) ? '17:00' : '',
  available: WEEKDAY_DAYS.has(day),
}));

/**
 * Renders a formset field. For "weekly_schedule" shows 7 day cards
 * with availability toggle and start/end time inputs.
 * Defaults to weekdays (Sun–Thu) 09:00–17:00.
 */
export const FormsetField: React.FC<FormsetFieldProps> = ({
  questionCode,
  value,
  onChange,
  locale = 'en',
  disabled = false,
  required = false,
  hasError = false,
}) => {
  const isAr = locale === 'ar';
  const dayLabels = isAr ? DAY_LABELS_AR : DAY_LABELS_EN;

  const slots: WeeklyScheduleSlot[] = useMemo(() => {
    if (Array.isArray(value) && value.length > 0) {
      const byDay = new Map(value.map((s) => [s.day, s]));
      return DAY_KEYS.map((day) => ({
        day,
        start_time: byDay.get(day)?.start_time ?? '',
        end_time: byDay.get(day)?.end_time ?? '',
        available: byDay.get(day)?.available ?? false,
      }));
    }
    return DEFAULT_SLOTS.map((s) => ({ ...s }));
  }, [value]);

  const updateSlot = useCallback(
    (index: number, patch: Partial<WeeklyScheduleSlot>) => {
      const next = slots.map((slot, i) =>
        i === index ? { ...slot, ...patch } : slot
      );
      onChange(next);
    },
    [slots, onChange]
  );

  // The UI renders DEFAULT_SLOTS (Sun–Thu, 09:00–17:00) when there's no value
  // yet, but the form field stays undefined until the user toggles a day — so
  // submit-time validation ("at least one day available") fails even though
  // days appear selected. Seed the defaults into the form once so the displayed
  // state and the form state agree.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (questionCode !== 'weekly_schedule') return;
    if (!Array.isArray(value) || value.length === 0) {
      seededRef.current = true;
      onChange(DEFAULT_SLOTS.map((s) => ({ ...s })));
    }
  }, [questionCode, value, onChange]);

  if (questionCode !== 'weekly_schedule') {
    return (
      <div className="rounded-xl border border-dashed border-muted-foreground/30 bg-muted/20 p-5 text-sm text-muted-foreground">
        {isAr ? 'نوع النموذج غير مدعوم.' : 'Unsupported formset type.'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 text-muted-foreground">
        <CalendarDays className="h-4 w-4 shrink-0" />
        <p className="text-sm">
          {isAr
            ? 'حدد أوقات العمل لكل يوم. فعّل اليوم واضبط من–إلى إذا كان متاحاً.'
            : 'Set working hours per day. Turn on a day and set from–to when available.'}
        </p>
      </div>

      {/* Day cards */}
      <div
        className={cn(
          'rounded-xl border bg-card shadow-sm overflow-hidden transition-colors',
          hasError && 'border-destructive ring-1 ring-destructive/20'
        )}
      >
        <ul className="divide-y divide-border/80">
          {slots.map((slot, index) => {
            const isWeekend = WEEKEND_DAYS.has(slot.day);
            const isAvailable = slot.available ?? false;
            return (
              <li
                key={slot.day}
                className={cn(
                  'transition-colors',
                  isAvailable && 'bg-primary/[0.03]',
                  isWeekend && !isAvailable && 'bg-muted/30'
                )}
              >
                <div
                  className={cn(
                    'flex flex-col gap-2 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:px-5 sm:py-4',
                    isAr && 'sm:flex-row-reverse'
                  )}
                >
                  {/* Day name + available toggle */}
                  <div
                    className={cn(
                      'flex min-w-0 items-center gap-3 sm:flex-1',
                      isAr && 'flex-row-reverse'
                    )}
                  >
                    <span
                      className={cn(
                        'w-20 shrink-0 text-sm font-medium sm:w-28',
                        isAvailable ? 'text-foreground' : 'text-muted-foreground',
                        isWeekend && 'text-muted-foreground'
                      )}
                    >
                      {dayLabels[slot.day] ?? slot.day}
                    </span>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2',
                        disabled && 'cursor-not-allowed opacity-70'
                      )}
                    >
                      <Checkbox
                        checked={isAvailable}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          updateSlot(index, {
                            available: checked === true,
                            ...(checked !== true && { start_time: '', end_time: '' }),
                          })
                        }
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <span className="text-xs text-muted-foreground">
                        {isAr ? 'متاح' : 'Available'}
                      </span>
                    </label>
                  </div>

                  {/* Time inputs - only when available */}
                  <div
                    className={cn(
                      'flex items-center gap-2 sm:gap-3 ps-1 sm:ps-0',
                      isAr && 'flex-row-reverse'
                    )}
                  >
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 sm:h-4 sm:w-4" />
                      <Input
                        type="time"
                        value={slot.start_time}
                        disabled={disabled || !isAvailable}
                        className={cn(
                          'h-8 w-[6.5rem] font-mono text-xs transition-opacity sm:h-9 sm:w-[7.5rem] sm:text-sm',
                          !isAvailable && 'opacity-50'
                        )}
                        onChange={(e) =>
                          updateSlot(index, { start_time: e.target.value })
                        }
                      />
                    </div>
                    <span className="text-muted-foreground/60 text-xs font-medium">
                      {isAr ? '←' : '–'}
                    </span>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Input
                        type="time"
                        value={slot.end_time}
                        disabled={disabled || !isAvailable}
                        className={cn(
                          'h-8 w-[6.5rem] font-mono text-xs transition-opacity sm:h-9 sm:w-[7.5rem] sm:text-sm',
                          !isAvailable && 'opacity-50'
                        )}
                        onChange={(e) =>
                          updateSlot(index, { end_time: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {required && (
        <p className="text-xs text-muted-foreground">
          {isAr
            ? '* مطلوب تحديد يوم واحد متاح على الأقل'
            : '* At least one day must be set as available'}
        </p>
      )}
    </div>
  );
};
